import { useState, useEffect } from 'react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import CronEditor from '@/components/admin/CronEditor';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

const TASK_TYPES = [
  { label: 'Supplier Discovery', value: 'supplier_discovery' },
  { label: 'Catalog Curation', value: 'catalog_curation' },
  { label: 'Pricing Review', value: 'pricing_review' },
  { label: 'Order Fulfillment', value: 'order_fulfillment' },
  { label: 'Catalog Sync', value: 'catalog_sync' },
  { label: 'Supplier Research', value: 'supplier_research' },
  { label: 'Product Listing', value: 'product_listing' },
];

interface ScheduleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (schedule: any) => void;
  editingSchedule?: any | null;
}

interface FormState {
  name: string;
  description: string;
  task_type: string;
  cron_expression: string;
  context: string;
}

const defaultForm: FormState = {
  name: '',
  description: '',
  task_type: 'supplier_discovery',
  cron_expression: '0 3 * * *',
  context: '',
};

function validateJson(str: string): string | null {
  if (!str.trim()) return null;
  try {
    JSON.parse(str);
    return null;
  } catch {
    return 'Invalid JSON';
  }
}

export default function ScheduleFormModal({ isOpen, onClose, onSaved, editingSchedule }: ScheduleFormModalProps) {
  const addToast = useUIStore((s) => s.addToast);
  const [form, setForm] = useState<FormState>(defaultForm);
  const [isSaving, setIsSaving] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (editingSchedule) {
        setForm({
          name: editingSchedule.name,
          description: editingSchedule.description ?? '',
          task_type: editingSchedule.task_type,
          cron_expression: editingSchedule.cron_expression,
          context: editingSchedule.context ? JSON.stringify(editingSchedule.context, null, 2) : '',
        });
      } else {
        setForm(defaultForm);
      }
      setJsonError(null);
    }
  }, [isOpen, editingSchedule]);

  const handleContextChange = (value: string) => {
    setForm((f) => ({ ...f, context: value }));
    setJsonError(validateJson(value));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      addToast('Name is required', 'error');
      return;
    }
    const jsonErr = validateJson(form.context);
    if (jsonErr) {
      setJsonError(jsonErr);
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        task_type: form.task_type,
        cron_expression: form.cron_expression,
        context: form.context.trim() ? JSON.parse(form.context) : undefined,
      };

      let saved: any;
      if (editingSchedule) {
        saved = await api.put(`/admin/agent/schedules/${editingSchedule.id}`, payload);
        addToast('Schedule updated', 'success');
      } else {
        saved = await api.post('/admin/agent/schedules', payload);
        addToast('Schedule created', 'success');
      }

      onSaved(saved);
      onClose();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to save schedule', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingSchedule ? 'Edit Schedule' : 'Create Schedule'}
      className="max-w-xl"
    >
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g., Daily Pricing Review"
            className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Optional description of what this schedule does..."
            className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            rows={2}
          />
        </div>

        {/* Task Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
          <select
            value={form.task_type}
            onChange={(e) => setForm((f) => ({ ...f, task_type: e.target.value }))}
            className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            {TASK_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Cron Expression */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
          <CronEditor
            value={form.cron_expression}
            onChange={(val) => setForm((f) => ({ ...f, cron_expression: val }))}
          />
        </div>

        {/* Context JSON */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Context <span className="text-gray-400 font-normal">(JSON, optional)</span>
          </label>
          <textarea
            value={form.context}
            onChange={(e) => handleContextChange(e.target.value)}
            placeholder={'{\n  "category": "vegetables"\n}'}
            className={`w-full text-sm font-mono border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 ${
              jsonError ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
            }`}
            rows={4}
            spellCheck={false}
          />
          {jsonError && <p className="text-xs text-red-600 mt-1">{jsonError}</p>}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !!jsonError}>
            {isSaving ? 'Saving...' : editingSchedule ? 'Save Changes' : 'Create Schedule'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
